import uniqBy from 'lodash.uniqby';

import {
  ProjectDuplicationStatus,
  ProjectMemberAttributes,
  ProjectMemberRole,
  ProjectMemberRoleAttributes,
  TeamMemberRole,
  TeamRole,
} from '@graphql/generated';

export const selectableProjectRoles = Object.values(ProjectMemberRole).filter(
  (r) => r !== ProjectMemberRole.Owner
);
export const defaultProjectRole = ProjectMemberRole.Editor;

// Several GraphQL fields type member roles as plain String (e.g.
// UserRequest.role), so values may arrive in any casing. Normalize to a
// ProjectMemberRole enum value, or null when unrecognized.
export const toProjectMemberRole = (
  role: string | null | undefined
): ProjectMemberRole | null => {
  if (!role) return null;
  const normalized = role.toUpperCase() as ProjectMemberRole;
  return Object.values(ProjectMemberRole).includes(normalized)
    ? normalized
    : null;
};
export const getProjectRoleEnumValue = (role: string) => {
  const indexOfRole = Object.values(ProjectMemberRole).indexOf(
    role as ProjectMemberRole
  );
  return Object.values(ProjectMemberRole)[indexOfRole];
};

// Assigning or adding the Admin role is reserved for Project Owner or Team
// Owner/Admin. A Project Admin canNOT assign or add Admin — only a Team Admin
// (or either Owner) can (TA-9240; Project Admin excluded in TA-9932).
// TeamRole and TeamMemberRole are identical string enums; both appear across
// different GraphQL types so the second param accepts either.
export const canUpdateAdminRole = (
  currentUserProjectRole: ProjectMemberRole | null | undefined,
  currentUserTeamRole: TeamMemberRole | TeamRole | null | undefined
): boolean =>
  currentUserProjectRole === ProjectMemberRole.Owner ||
  currentUserTeamRole === TeamMemberRole.Owner ||
  currentUserTeamRole === TeamMemberRole.Admin;

// Changing a project member's role is reserved for Project Owner/Admin or
// Team Owner/Admin. Project Editors and Team Members may add/remove members
// (see canRemoveProjectMember) but never change roles.
export const canChangeProjectMemberRole = (
  currentUserProjectRole: ProjectMemberRole | null | undefined,
  currentUserTeamRole: TeamMemberRole | TeamRole | null | undefined
): boolean =>
  currentUserProjectRole === ProjectMemberRole.Owner ||
  currentUserProjectRole === ProjectMemberRole.Admin ||
  currentUserTeamRole === TeamMemberRole.Owner ||
  currentUserTeamRole === TeamMemberRole.Admin;

// The project Owner can never be removed. Removing an Admin requires
// Admin-management rights (canUpdateAdminRole), so a Project Admin cannot
// remove a fellow Admin — only Project Owner or Team Owner/Admin can; this
// keeps removal symmetric with role change (TA-9932). For non-Admin targets
// (Editor/Viewer): Project Owner/Admin or any team role may remove them, and
// a Project Editor may remove fellow Editors and Viewers.
export const canRemoveProjectMember = (
  currentUserProjectRole: ProjectMemberRole | null | undefined,
  currentUserTeamRole: TeamMemberRole | TeamRole | null | undefined,
  targetRole: ProjectMemberRole | null | undefined
): boolean => {
  if (!targetRole || targetRole === ProjectMemberRole.Owner) return false;
  if (targetRole === ProjectMemberRole.Admin) {
    return canUpdateAdminRole(currentUserProjectRole, currentUserTeamRole);
  }
  const viaTeamRole =
    currentUserTeamRole === TeamMemberRole.Owner ||
    currentUserTeamRole === TeamMemberRole.Admin ||
    currentUserTeamRole === TeamMemberRole.Member;
  const viaProjectRole =
    currentUserProjectRole === ProjectMemberRole.Owner ||
    currentUserProjectRole === ProjectMemberRole.Admin ||
    currentUserProjectRole === ProjectMemberRole.Editor;
  return viaTeamRole || viaProjectRole;
};

// Pending-invite management (resend/cancel) follows the member-removal
// matrix per invited role (TA-9932). Kept as a separate helper because the
// two concepts have diverged before and call sites should stay explicit.
export const canManageProjectInvite = (
  currentUserProjectRole: ProjectMemberRole | null | undefined,
  currentUserTeamRole: TeamMemberRole | TeamRole | null | undefined,
  invitedRole: ProjectMemberRole | null | undefined
): boolean =>
  canRemoveProjectMember(
    currentUserProjectRole,
    currentUserTeamRole,
    invitedRole
  );

// The backend can return `members[].user: null` for a project the current
// user can see but is not an explicit member of (team-visible gating,
// TA-10106). The schema still declares `ProjectMember.user: User!`, so
// codegen types it non-null and TS cannot catch the deref (TA-10297). Drop
// entries we cannot render instead of making every consumer guard the
// element.
export const getRenderableProjectMembers = <T extends { user?: unknown }>(
  members?: T[] | null
): T[] => (members ?? []).filter((m) => !!m.user);

/**
 * The create-project mutation needs a team UUID for its preflight check, while
 * the form persists the team ID. Prefer the selected team's current UUID so a
 * recently changed team cannot submit against a stale form value; retain the
 * form value as a fallback while the teams list is catching up.
 */
export const resolveProjectTeamUuid = (
  teamId: string | null | undefined,
  formTeamUuid: string | null | undefined,
  teams: ReadonlyArray<{
    id?: string | null;
    uuid?: string | null;
  }>
): string | undefined =>
  teams.find((team) => team.id === teamId)?.uuid ?? formTeamUuid ?? undefined;

// TA-9974 stopped duplicateProject from raising when the background duplication
// job cannot be enqueued (e.g. Redis down): the mutation still returns the created
// project, only with duplicationStatus FAILED. Without this check a half-copied
// duplicate is indistinguishable from a good one, so the client would navigate the
// user into a project missing its tasks/files (TA-10496). Only projects created by
// duplicateProject carry the field at all — null/undefined is never a failure.
export const isProjectDuplicationFailed = (
  project?: {
    duplicationStatus?: ProjectDuplicationStatus | null;
  } | null
): boolean => project?.duplicationStatus === ProjectDuplicationStatus.Failed;

// TA-10800: on web, the same contact could end up selected twice — once via
// an individually-added contact Group, once via a second, overlapping Group
// — each copy independently assigned a role. Nothing upstream guaranteed
// these were deduped before reaching createProject/updateProject, and the
// backend's unique constraint on (project_id, user_id) turned the second
// copy into an unhandled crash with zero user-facing feedback (confirmed in
// production via Sentry: 60 occurrences / 1 account / 13 days). Applied as
// a last line of defense right where the mutation payload is assembled, in
// addition to fixing the actual duplicate-selection gap in the invite
// modal — keeps whichever entry appeared first.
//
// ProjectMemberAttributes also represents a *whole contact Group as a
// single member* (the mobile ContactListMobile flow, distinct from the web
// invite modal's per-user flattening): ProjectFormStack.submit maps each
// `{ type: 'GROUP', id: groupId }` selection to
// `{ userId: me.id, groupId }`, so two different selected groups share the
// same userId (the current user) but have different, non-empty groupIds.
// Keying purely on userId collapsed those to the first group and silently
// dropped the rest -- key on groupId when present so distinct groups always
// survive, falling back to userId for ordinary per-user rows (groupId ''
// or undefined).
export const dedupeProjectMembers = (
  members: ProjectMemberAttributes[]
): ProjectMemberAttributes[] =>
  uniqBy(members, (m) =>
    m.groupId ? `group:${m.groupId}` : `user:${m.userId}`
  );

export const dedupeProjectMemberInvites = (
  invites: ProjectMemberRoleAttributes[]
): ProjectMemberRoleAttributes[] =>
  uniqBy(invites, (pm) => pm.email.toLowerCase());
